import { lazy, Suspense, type ReactNode } from "react";
import { ICON_MAP } from "./icon-map";

/**
 * O mapa do portal (~380 ícones) é carregado sob demanda, num chunk separado
 * do bundle de toda página. Com SSR o servidor já entrega o `<svg>` no HTML e a
 * hidratação mantém esse HTML até o chunk chegar — sem pop-in. O fallback do
 * Suspense só aparece em montagens client-side (ex.: um painel aberto depois)
 * e ocupa o mesmo quadrado do ícone, sem deslocar o layout.
 */
const PortalIcon = lazy(() => import("./PortalIcon"));

interface IconProps {
    /** Chave do ICON_MAP ou nome de export do react-icons gravado pelo portal (ex.: "MdLocalOffer"). */
    name?: string | null;
    size?: number;
    className?: string;
    /** O que renderizar quando o nome não resolve para nenhum ícone. */
    fallback?: ReactNode;
    /** Log temporário para diagnosticar a resolução de ícones dinâmicos. */
    debug?: boolean;
}

/** Resolve uma chave de ícone (string vinda de dados) para o componente react-icons correspondente. */
export function Icon({ name, size = 20, className, fallback = null, debug = false }: IconProps) {
    if (!name) return fallback;

    const IconComponent = ICON_MAP[name];
    if (IconComponent) return <IconComponent size={size} className={className} />;

    return (
        <Suspense
            fallback={
                <span
                    aria-hidden
                    className={className}
                    style={{ display: "inline-block", width: size, height: size }}
                />
            }
        >
            <PortalIcon name={name} size={size} className={className} fallback={fallback} debug={debug} />
        </Suspense>
    );
}
